import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, tokenStore } from "../lib/api";

const AuthContext = createContext(null);

// Auth flow:
// 1. On mount, if a token exists in sessionStorage we call /auth/me to validate it.
// 2. login/register get an access_token from the backend, stored in sessionStorage.
// 3. The token is sent as Authorization: Bearer on every request.
// 4. The backend also sets httpOnly cookies — these will take over automatically
//    when this app is deployed on its own domain (preview platform's edge CORS=*
//    currently blocks credentialed cookie flows).
//
// State semantics: null = checking, false = anonymous, object = authenticated user.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_err) {
      // Invalid/expired token — clear it silently (401 is expected when session is gone)
      tokenStore.clear();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) tokenStore.set(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) tokenStore.set(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout API call failed:", e?.message);
    }
    tokenStore.clear();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
